// Humanteq funnel per the approved 2-screen mock (mattermost 2026-10-23):
// step 1 name + communication style chips («Спокойно и аналитично» default,
// §0.2), step 2 topics — the gender question and the «Всё готово» screen are
// gone (PR #41 flow). Registration follows step 2 for guests: they go to
// /register straight from the topics step, and the post-auth gate submits
// the frozen draft. No state score: the greeting carries the name, the
// style and the topics.
import { useEffect, useState, type ChangeEvent, type MouseEvent } from 'react'
import { engineStyleApi } from '../../../api/chat'
import { useAuthStore } from '../../../store/authStore'
import * as metrica from '../../../lib/metrica'
import type { OnboardingScenarioProps } from './types'
import { COMMUNICATION_STYLES, DEFAULT_STYLE } from './communicationStyles'
import { clearLandingStartTopics, readLandingStartTopics } from './startTopics'

export function useOnboardingFlow({ payload, onChange: change, onComplete }: OnboardingScenarioProps) {
  const [styleLabels, setStyleLabels] = useState(COMMUNICATION_STYLES)
  const [styleStepAvailable, setStyleStepAvailable] = useState(true)
  useEffect(() => {
    // Guests have no token — /engine/style needs auth, so don't fire a doomed
    // request; the bundled labels match the stand's style_labels.txt anyway.
    if (!useAuthStore.getState().accessToken) return undefined
    let active = true
    engineStyleApi.get().then((state) => {
      if (!active) return
      if (state?.labels) setStyleLabels(state.labels)
      setStyleStepAvailable(state?.enabled !== false)
    }).catch(() => { /* fail-open: a transient error must not collapse the style block mid-screen */ })
    return () => { active = false }
  }, [])
  useEffect(() => {
    // Landing pages write humanteq.startTopics before the PWA opens; step 2
    // preselects those chips. Only fills an empty draft so a persisted choice
    // from an earlier session is never clobbered.
    const landing = readLandingStartTopics()
    if (!landing.length || (payload.tags ?? []).length) return
    change({ ...payload, tags: landing })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const step = payload.step ?? 1
  const name = payload.displayName ?? ''
  const tags = payload.tags ?? []
  const styleKind = payload.communicationStyle ?? DEFAULT_STYLE
  const goNext = () => {
    metrica.reachGoal(`onboarding_step_${step + 1}`)
    change({ ...payload, step: step + 1 })
  }
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => change({ ...payload, displayName: event.target.value })
  const toggleTag = (event: MouseEvent<HTMLButtonElement>) => {
    const tag = event.currentTarget.dataset.tag!
    change({ ...payload, tags: tags.includes(tag) ? tags.filter((value) => value !== tag) : [...tags, tag] })
  }
  const changeStyle = (event: MouseEvent<HTMLButtonElement>) => change({ ...payload, communicationStyle: event.currentTarget.dataset.kind })
  const finishPayload = () => ({ ...payload, communicationStyle: styleStepAvailable ? styleKind : null })
  // Step 2 exit (approved mock): guests hand the frozen draft to /register —
  // registration is the funnel's follow-on; signed-in users finish here.
  const continueFromTopics = () => {
    if (!tags.length) return
    clearLandingStartTopics()
    if (!useAuthStore.getState().accessToken) metrica.reachGoal('onboarding_step_3')
    else metrica.reachGoal('onboarding_complete')
    onComplete(finishPayload())
  }
  const action = step === 1
    ? { label: 'Продолжить', onClick: goNext, disabled: !name.trim() }
    : {
        label: useAuthStore.getState().accessToken ? 'Начать беседу' : 'Продолжить',
        onClick: continueFromTopics,
        disabled: tags.length === 0,
      }
  return { step, name, tags, styleKind, styleLabels, styleStepAvailable, action, handleNameChange, toggleTag, changeStyle }
}
